import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import SearchableSelect from '@/Components/SearchableSelect';
import StatWidget, { StatWidgetGrid } from '@/Components/StatWidget';
import Avatar from '@/Components/Avatar';
import Badge from '@/Components/Badge';
import EmptyState from '@/Components/EmptyState';
import QuickActions from '@/Components/QuickActions';
import Pagination from '@/Components/Pagination';
import SearchInput from '@/Components/SearchInput';
import ConfirmModal from '@/Components/ConfirmModal';

export default function ClientsIndex({ clients, countries, users, sources, stats, filters }) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);

    const handleSearch = (value) => {
        router.get('/clients', { ...filters, search: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleFilter = (key, value) => {
        router.get('/clients', { ...filters, [key]: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleSort = (column) => {
        const direction = filters.sort === column && filters.direction === 'asc' ? 'desc' : 'asc';
        router.get('/clients', { ...filters, sort: column, direction }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleToggleStatus = (client) => {
        router.patch(`/clients/${client.id}/toggle-status`, {}, { preserveScroll: true });
    };

    const handleDelete = () => {
        router.delete(`/clients/${clientToDelete.id}`, {
            onSuccess: () => {
                setShowDeleteModal(false);
                setClientToDelete(null);
            },
        });
    };

    const sourceOptions = Object.entries(sources).map(([key, label]) => ({ id: key, name: label }));

    return (
        <AppLayout title="Clients">
            <PageHeader
                title="Clients"
                subtitle="Manage your client database"
                breadcrumbs={[
                    { label: 'CRM' },
                    { label: 'Clients' },
                ]}
                actions={
                    <Link href="/clients/create" className="btn btn-primary">
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Client
                    </Link>
                }
            />

            {/* Stats Row */}
            <StatWidgetGrid columns={4} className="mb-4">
                <StatWidget
                    label="Total Clients"
                    value={stats.total}
                    icon="bi-people"
                    variant="primary"
                />
                <StatWidget
                    label="Active Clients"
                    value={stats.active}
                    icon="bi-person-check"
                    variant="success"
                />
                <StatWidget
                    label="This Month"
                    value={stats.this_month}
                    icon="bi-calendar-plus"
                    variant="info"
                />
                <StatWidget
                    label="Inactive Clients"
                    value={stats.total - stats.active}
                    icon="bi-person-dash"
                    variant="warning"
                />
            </StatWidgetGrid>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <SearchInput
                                value={filters.search || ''}
                                onSearch={handleSearch}
                                placeholder="Name, email, phone..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Source</label>
                            <SearchableSelect
                                options={sourceOptions}
                                value={filters.source || ''}
                                onChange={(value) => handleFilter('source', value)}
                                placeholder="All Sources"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Country</label>
                            <SearchableSelect
                                options={countries}
                                value={filters.country || ''}
                                onChange={(value) => handleFilter('country', value)}
                                placeholder="All Countries"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Assigned To</label>
                            <SearchableSelect
                                options={users}
                                value={filters.assigned_to || ''}
                                onChange={(value) => handleFilter('assigned_to', value)}
                                placeholder="All Users"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Status</label>
                            <select
                                className="form-select"
                                value={filters.status || ''}
                                onChange={(e) => handleFilter('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <button
                                onClick={() => router.get('/clients')}
                                className="btn btn-outline-secondary w-100"
                                title="Clear filters"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Clients Table */}
            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th className="cursor-pointer" onClick={() => handleSort('name')}>
                                    Client
                                    {filters.sort === 'name' && (
                                        <i className={`bi bi-chevron-${filters.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                </th>
                                <th>Contact</th>
                                <th>Location</th>
                                <th>Source</th>
                                <th>Assigned To</th>
                                <th className="text-center">Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.data.length === 0 ? (
                                <tr>
                                    <td colSpan="8">
                                        <EmptyState
                                            icon="bi-people"
                                            title="No clients found"
                                            description="Start building your client database by adding your first client."
                                            action={
                                                <Link href="/clients/create" className="btn btn-primary">
                                                    <i className="bi bi-plus-lg me-1"></i>
                                                    Add Client
                                                </Link>
                                            }
                                        />
                                    </td>
                                </tr>
                            ) : (
                                clients.data.map((client, index) => (
                                    <tr key={client.id}>
                                        <td className="text-muted">
                                            {(clients.current_page - 1) * clients.per_page + index + 1}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <Avatar name={client.name} size="md" />
                                                <div>
                                                    <Link href={`/clients/${client.id}`} className="fw-semibold text-decoration-none text-dark">
                                                        {client.name}
                                                    </Link>
                                                    {client.company && (
                                                        <div className="text-muted small">{client.company}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                {client.email && (
                                                    <div><i className="bi bi-envelope me-1 text-muted"></i>{client.email}</div>
                                                )}
                                                {client.phone && (
                                                    <div><i className="bi bi-phone me-1 text-muted"></i>{client.phone}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {client.country && (
                                                <Badge variant="light">{client.country.name}</Badge>
                                            )}
                                            {client.city && (
                                                <div className="text-muted small mt-1">{client.city}</div>
                                            )}
                                        </td>
                                        <td>
                                            {client.source && (
                                                <Badge variant="info-subtle">{sources[client.source]}</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {client.assigned_to && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <Avatar name={client.assigned_to.name} size="xs" />
                                                    <span className="small">{client.assigned_to.name}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Badge variant={client.is_active ? 'success-subtle' : 'secondary'}>
                                                {client.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="text-end">
                                            <QuickActions
                                                actions={[
                                                    { icon: 'bi-eye', label: 'View', onClick: () => router.visit(`/clients/${client.id}`) },
                                                    { icon: 'bi-pencil', label: 'Edit', onClick: () => router.visit(`/clients/${client.id}/edit`) },
                                                    { icon: client.is_active ? 'bi-toggle-off' : 'bi-toggle-on', label: client.is_active ? 'Deactivate' : 'Activate', onClick: () => handleToggleStatus(client) },
                                                    { divider: true },
                                                    { icon: 'bi-trash', label: 'Delete', danger: true, onClick: () => { setClientToDelete(client); setShowDeleteModal(true); } },
                                                ]}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    data={clients}
                    showPerPage
                    onPerPageChange={(value) => handleFilter('per_page', value)}
                />
            </div>

            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Client"
                message={`Are you sure you want to delete ${clientToDelete?.name}? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />
        </AppLayout>
    );
}
